import { IWebContentResponseMessage, IWebContentRequestMessage, FrameContexts } from './messages.interface';
import { EventListener } from './event-listener';
import { isValidUrlForApp } from './utils';
import {
  IAppDefinition,
  IHostedWebContentAuthSdkService,
  IWebContentContext,
} from './hosted-web-content-sdk.interface';
import { IBaseAuthenticationService, ITelemetryService } from './interfaces';
import { IMessageProcessor } from './MessageHandling/messaging-infra.interface';
import { IWindowProvider } from './event-listener.interface';
import { AuthFrameMessageProcessor } from './MessageHandling/message-processor-auth';
import { WebContentSdkService } from './web-content-sdk';

// Time between pinging the auth window in ms
const pingTime = 100;

export enum AuthFailedReason {
  AppDefinitionUnavailable = 'AppDefinitionUnavailable',
  BlockedByBrowser = 'BlockedByBrowser',
  CancelledByUser = 'CancelledByUser',
  InvalidAuthUrl = 'InvalidAuthUrl',
  InvalidFrameContext = 'InvalidFrameContext',
  Other = 'Other',
}

export interface IWindowAuthenticationService extends IBaseAuthenticationService {
  /**
   * success call when user authenticate passes a data to the host.
   * Hubs are required to provide implementation if the app is launched in authentication frameContext

   * @param {JSON} data - The date selected by user.
   * @returns {Promise<void>}
   */
  notifySuccess(data: string): Promise<void>;

  /**
   * failure call when there is issue acquiring data from the auth.
   * Hubs are required to provide implementation if the app is launched in authentication frameContext
   * @returns {Promise<void>}
   */
  notifyFailure(reason: string, failureReason?: AuthFailedReason): Promise<void>;
}

export interface IWindowInfo {
  /**
   * url of new window.
   */
  url: string;
  /**
   * height of new window. default is fullScreen
   */
  height?: number;
  /**
   * width of new window. default is fullScreen
   */
  width?: number;
  /**
   * left coordinate where the new window has to be shown
   */
  left?: number;
  /**
   * top coordinate where the new window has to be shown
   */
  top?: number;
}

export class HostedWebContentAuthSdkService extends WebContentSdkService implements IHostedWebContentAuthSdkService {
  private globalWindowEventListener: EventListener | undefined;
  private authWindow?: Window | null;
  private authWindowMonitor?: number;

  protected messageProcessor?: IMessageProcessor;

  constructor(
    protected hostWindow: Window,
    protected openWindowInfo: IWindowInfo,
    protected appDefinition: IAppDefinition,
    protected authenticationService: IWindowAuthenticationService,
    protected context: IWebContentContext,
    telemetryService?: ITelemetryService
  ) {
    super(hostWindow, context, authenticationService, telemetryService, undefined);

    if (this.context.page.frameContext !== FrameContexts.authentication) {
      this.HandleError('Invalid frame context for scenario', AuthFailedReason.InvalidFrameContext);
    }
    if (!this.appDefinition) {
      this.HandleError('App definition not available', AuthFailedReason.AppDefinitionUnavailable);
    }
    if (isValidUrlForApp(openWindowInfo.url, appDefinition) && this.hostWindow) {
      const globalWindow = this.hostWindow;
      let windowFeatures = '';
      if (openWindowInfo.width && openWindowInfo.height) {
        windowFeatures = `width=${openWindowInfo.width},height=${openWindowInfo.height}`;
        // compared with undefined because someone can pass 0 in these parameters
        if (openWindowInfo.left != undefined && openWindowInfo.top != undefined) {
          windowFeatures = `${windowFeatures},left=${openWindowInfo.left},top=${openWindowInfo.top}`;
        }
      }

      this.authWindow = globalWindow.open(openWindowInfo.url, `appAuthWindow`, windowFeatures);
      if (this.authWindow) {
        this.origin = openWindowInfo.url;
        this.setUserClickTime(context.app.userClickTime ?? Date.now());
        this.setupAuthMessageHandlers();
        this.globalWindowEventListener = new EventListener(globalWindow);
        this.globalWindowEventListener.addListener('message', this.onAuthMessageReceived);
        this.startAuthWindowMonitor();
        this.initializeAuthenticationContext(context);
      } else {
        this.HandleError('browser is blocking the url to open', AuthFailedReason.BlockedByBrowser);
      }
    } else {
      this.HandleError('Auth url is not valid', AuthFailedReason.InvalidAuthUrl);
    }
  }

  public getHostWindow(): Window | IWindowProvider | null {
    return this.authWindow || null;
  }

  public postResponseToApp(id: number | undefined, args?: (string | boolean | object)[]): void {
    this.sendResponseMessage(id, args);
  }

  public isWindowOpen(): boolean {
    return this.authWindow ? !this.authWindow.closed : false;
  }

  /**
   * close auth window
   */
  public closeWindow(): void {
    this.unloadApp(this.appDefinition);
  }

  public onAuthMessageReceived = async (event: MessageEvent) => {
    if (!this.authWindow || this.authWindow !== event.source) {
      return;
    }

    const sourceOrigin = event.origin;
    if (sourceOrigin !== this.origin && isValidUrlForApp(sourceOrigin, this.appDefinition)) {
      this.origin = event.origin;
    }
    if (this.origin === sourceOrigin && this.messageProcessor) {
      await this.messageProcessor.process(event);
    }
  };

  /**
   * to send response message to frame
   * @param id: id used for callback function
   * @param args: function arguments
   */
  public sendResponseMessage(id: number | undefined, args?: (object | string | boolean)[]): void {
    if (this.origin) {
      const response: IWebContentResponseMessage = {
        id,
        args,
        origin: this.origin,
      };
      this.postMessage(response);
    }
  }

  /**
   * to send request message to frame
   * @param functionName: function name
   * @param funArguments: function arguments
   */
  public sendRequestMessage(functionName: string, funArguments?: (object | string)[]): void {
    if (this.origin) {
      const request: IWebContentRequestMessage = {
        func: functionName,
        args: funArguments,
      };
      this.postMessage(request);
    }
  }

  private startAuthWindowMonitor(): void {
    this.stopAuthWindowMonitor();

    this.authWindowMonitor = this.hostWindow.setInterval(() => {
      this.authWindowInterval();
    }, pingTime);
  }

  private authWindowInterval() {
    if (!this.isWindowOpen()) {
      this.HandleError('CancelledByUser', AuthFailedReason.CancelledByUser);
      this.stopAuthWindowMonitor();
    } else {
      // authentication window is pinged in order to restablish opener for SDK in case it is lost.
      this.sendRequestMessage('ping');
    }
  }

  public stopAuthWindowMonitor(): void {
    if (this.authWindowMonitor) {
      this.hostWindow.clearInterval(this.authWindowMonitor);
      this.authWindowMonitor = undefined;
    }
  }

  /**
   * function to execute when app frame is unloaded
   */
  public unloadApp(_appDefinition: IAppDefinition) {
    this.unloadSDK();
    this.authWindow?.close();
  }

  /**
   * function to execute when app container is unmounted
   */
  public unloadSDK(): void {
    if (this.globalWindowEventListener) {
      this.globalWindowEventListener.removeListener('message');
    }
  }

  private setupAuthMessageHandlers = () => {
    if (this.authenticationService) {
      this.messageProcessor = new AuthFrameMessageProcessor(this, this.authenticationService, this.telemetryService);
    }
  };

  private postMessage(message: IWebContentRequestMessage | IWebContentResponseMessage): void {
    if (this.origin) {
      this.authWindow?.postMessage(message, this.origin);
    }
  }

  private HandleError(result: string, failureReason?: AuthFailedReason): void {
    if (this.authenticationService) {
      this.authenticationService.notifyFailure(result, failureReason);
    }
  }
}
